import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Menu from "../../components/globalComponents/headerMenu/menu";
import Footer from "../../components/globalComponents/footer/footer";
import GradientLine from "../../components/globalComponents/gradientLine/gradientLine";
import "./legalPage.scss";

const NAV = [
  { id: "aviso-legal", labelKey: "legal.sidebar.notice" },
  { id: "privacidad", labelKey: "legal.sidebar.privacy" },
  { id: "cookies", labelKey: "legal.sidebar.cookies" },
  { id: "gestion-cookies", labelKey: "legal.sidebar.cookieManagement" },
];

const LegalPage = () => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState("aviso-legal");

  const location = useLocation();

  // Scroll-spy con IntersectionObserver
  useEffect(() => {
    const observers = [];
    NAV.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveId(id); },
        { rootMargin: "-20% 0px -70% 0px" }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // Hash navigation — runs on mount and whenever hash changes (e.g. footer links)
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location.hash]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="legal-page">
      <Menu />

      <div className="legal-page__hero">
        <div className="legal-page__hero-inner">
          <h1 className="legal-page__hero-title">{t('legal.hero.title')}</h1>
          <p className="legal-page__hero-desc">
            {t('legal.hero.desc')}
          </p>
          <div className="legal-page__hero-meta">
            <span className="legal-page__meta-item">{t('legal.hero.updated')} <strong>{t('legal.hero.updatedDate')}</strong></span>
            <span className="legal-page__meta-item">{t('legal.hero.regulation')} <strong>{t('legal.hero.regulationNames')}</strong></span>
            <span className="legal-status">
              <span className="legal-status__dot" />
              {t('legal.hero.status')}
            </span>
          </div>
        </div>
      </div>

      <div className="legal-page__layout">

        {/* SIDEBAR */}
        <aside className="legal-sidebar">
          <p className="legal-sidebar__title">{t('legal.sidebar.title')}</p>
          <nav>
            <ul className="legal-sidebar__nav">
              {NAV.map(({ id, labelKey }) => (
                <li key={id}>
                  <button
                    className={`legal-sidebar__link ${activeId === id ? "legal-sidebar__link--active" : ""}`}
                    onClick={() => scrollTo(id)}
                  >
                    {t(labelKey)}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
          <div className="legal-sidebar__divider" />
          <div className="legal-sidebar__contact-card">
            <span className="legal-sidebar__contact-label bg-transparent">{t('legal.sidebar.questions')}</span>
            <a href="mailto:info@daromainnovations.com" className="legal-sidebar__contact-link bg-transparent">
              info@daromainnovations.com
            </a>
          </div>
        </aside>

        {/* CONTENIDO */}
        <main className="legal-content">

          {/* ── AVISO LEGAL ──────────────────────────────── */}
          <section id="aviso-legal" className="legal-section">
            <div className="legal-section__header">
              <h2 className="legal-section__title">Aviso Legal</h2>
              <span className="legal-section__ref">LSSI-CE Art. 10</span>
            </div>

            <div className="legal-block">
              <h3>1. Datos identificativos</h3>
              <p>En cumplimiento del artículo 10 de la Ley 34/2002 de Servicios de la Sociedad de la Información y del Comercio Electrónico (LSSI-CE), se facilitan los siguientes datos:</p>
              <div className="legal-data-grid">
                <div className="legal-data-item"><span>Razón social</span><strong>Daroma Innovations, S.L.</strong></div>
                <div className="legal-data-item"><span>Domicilio</span><strong>España</strong></div>
                <div className="legal-data-item"><span>Email</span><strong><a href="mailto:info@daromainnovations.com">info@daromainnovations.com</a></strong></div>
                <div className="legal-data-item"><span>Web</span><strong>daromainnovations.com</strong></div>
              </div>
            </div>

            <div className="legal-block">
              <h3>2. Objeto y ámbito de aplicación</h3>
              <p>El presente Aviso Legal regula el acceso y uso del sitio web <strong>daromainnovations.com</strong>, titularidad de Daroma Innovations, S.L. El acceso al sitio web implica la aceptación plena y sin reservas de las condiciones aquí establecidas.</p>
            </div>

            <div className="legal-block">
              <h3>3. Propiedad intelectual e industrial</h3>
              <p>Todos los contenidos del sitio web —textos, imágenes, gráficos, logotipos, software y cualquier otro material— son propiedad de Daroma Innovations, S.L. o de terceros que han autorizado su uso, y están protegidos por la legislación española e internacional sobre propiedad intelectual.</p>
              <div className="legal-callout legal-callout--warn">
                <span className="legal-callout__label">Prohibido</span>
                Queda expresamente prohibida la reproducción, distribución, comunicación pública o transformación de dichos contenidos sin la autorización expresa y por escrito de Daroma Innovations, S.L.
              </div>
            </div>

            <div className="legal-block">
              <h3>4. Condiciones de uso</h3>
              <ul className="legal-list">
                <li>Hacer un uso lícito del sitio web, conforme a la ley, la moral y las buenas costumbres.</li>
                <li>No realizar actividades que puedan dañar, inutilizar o deteriorar el sitio web o sus contenidos.</li>
                <li>No introducir o difundir contenidos falsos, difamatorios, obscenos o que vulneren derechos de terceros.</li>
                <li>No intentar acceder a áreas restringidas del sitio sin la debida autorización.</li>
              </ul>
            </div>

            <div className="legal-block">
              <h3>5. Legislación aplicable y jurisdicción</h3>
              <p>Las presentes condiciones se rigen por la legislación española. Para la resolución de cualquier controversia, las partes se someten a los Juzgados y Tribunales competentes conforme a la normativa vigente.</p>
            </div>
          </section>

          {/* ── POLÍTICA DE PRIVACIDAD ────────────────────── */}
          <section id="privacidad" className="legal-section">
            <div className="legal-section__header">
              <h2 className="legal-section__title">Política de Privacidad</h2>
              <span className="legal-section__ref">RGPD · LOPDGDD</span>
            </div>

            <div className="legal-callout legal-callout--info">
              <span className="legal-callout__label">Compromiso</span>
              En Daroma Innovations, S.L. nos comprometemos a proteger y respetar tu privacidad conforme al Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018 (LOPDGDD).
            </div>

            <div className="legal-block">
              <h3>1. Responsable del tratamiento</h3>
              <div className="legal-contact-box">
                <div className="legal-contact-box__icon">✉</div>
                <div>
                  <div className="legal-contact-box__label">Daroma Innovations, S.L.</div>
                  <a href="mailto:info@daromainnovations.com" className="legal-contact-box__value">info@daromainnovations.com</a>
                </div>
              </div>
            </div>

            <div className="legal-block">
              <h3>2. Datos que recopilamos</h3>
              <ul className="legal-list">
                <li>Nombre y apellidos</li>
                <li>Dirección de correo electrónico</li>
                <li>Número de teléfono (si se facilita)</li>
                <li>Datos de navegación y uso (cookies técnicas y analíticas)</li>
                <li>Información facilitada a través del formulario de contacto</li>
              </ul>
            </div>

            <div className="legal-block">
              <h3>3. Finalidad del tratamiento</h3>
              <ul className="legal-list">
                <li>Gestionar consultas y solicitudes enviadas</li>
                <li>Prestar los servicios contratados</li>
                <li>Enviar comunicaciones comerciales, si has dado tu consentimiento</li>
                <li>Mejorar la web y servicios mediante análisis de uso</li>
                <li>Cumplir con obligaciones legales</li>
              </ul>
            </div>

            <div className="legal-block">
              <h3>4. Base legal del tratamiento</h3>
              <div className="legal-table-wrap">
                <table className="legal-table">
                  <thead>
                    <tr><th>Base legal</th><th>Supuesto</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Consentimiento</td><td>Formulario de contacto o suscripción a comunicaciones</td></tr>
                    <tr><td>Ejecución de contrato</td><td>Prestación de servicios contratados</td></tr>
                    <tr><td>Interés legítimo</td><td>Mejora de la web y prevención de fraudes</td></tr>
                    <tr><td>Obligación legal</td><td>Cumplimiento de la normativa aplicable</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="legal-block">
              <h3>5. Conservación de los datos</h3>
              <p>Conservaremos tus datos durante el tiempo necesario para cumplir con la finalidad para la que fueron recogidos y, en todo caso, durante los plazos legalmente establecidos.</p>
            </div>

            <div className="legal-block">
              <h3>6. Tus derechos</h3>
              <div className="legal-rights-list">
                {[
                  { num: "A", title: "Acceso", desc: "Conocer qué datos tratamos sobre ti." },
                  { num: "R", title: "Rectificación", desc: "Corregir datos inexactos o incompletos." },
                  { num: "S", title: "Supresión", desc: "Solicitar la eliminación de tus datos." },
                  { num: "O", title: "Oposición", desc: "Oponerte al tratamiento en determinados supuestos." },
                  { num: "P", title: "Portabilidad", desc: "Recibir tus datos en formato estructurado." },
                  { num: "L", title: "Limitación", desc: "Solicitar que restrinjamos el uso de tus datos." },
                ].map(({ num, title, desc }) => (
                  <div className="legal-right-row" key={num}>
                    <div className="legal-right-row__num">{num}</div>
                    <div>
                      <div className="legal-right-row__title">{title}</div>
                      <div className="legal-right-row__desc">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: "16px" }}>
                Para ejercer tus derechos, escríbenos a{" "}
                <a href="mailto:info@daromainnovations.com">info@daromainnovations.com</a>. También puedes
                presentar una reclamación ante la{" "}
                <strong>Agencia Española de Protección de Datos (AEPD)</strong> en{" "}
                <a href="https://www.aepd.es" target="_blank" rel="noreferrer">www.aepd.es</a>.
              </p>
            </div>
          </section>

          {/* ── POLÍTICA DE COOKIES ───────────────────────── */}
          <section id="cookies" className="legal-section">
            <div className="legal-section__header">
              <h2 className="legal-section__title">Política de Cookies</h2>
              <span className="legal-section__ref">LSSI-CE · RGPD</span>
            </div>

            <div className="legal-block">
              <h3>¿Qué son las cookies?</h3>
              <p>Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas un sitio web. Permiten que el sitio recuerde tus preferencias y acciones durante un período de tiempo determinado.</p>
            </div>

            <div className="legal-block">
              <h3>Tipos de cookies que utilizamos</h3>
              <div className="legal-table-wrap">
                <table className="legal-table">
                  <thead>
                    <tr><th>Categoría</th><th>Descripción</th><th>Estado</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><strong>Técnicas</strong></td>
                      <td>Imprescindibles para el funcionamiento de la web: sesión, seguridad, navegación.</td>
                      <td><span className="legal-tag legal-tag--ok">Siempre activas</span></td>
                    </tr>
                    <tr>
                      <td><strong>Analíticas</strong></td>
                      <td>Permiten conocer cómo interactúan los usuarios con la web (páginas visitadas, tiempo de permanencia).</td>
                      <td><span className="legal-tag legal-tag--optional">Opcional</span></td>
                    </tr>
                    <tr>
                      <td><strong>Personalización</strong></td>
                      <td>Recuerdan tus preferencias (idioma, región, ajustes visuales) para futuras visitas.</td>
                      <td><span className="legal-tag legal-tag--optional">Opcional</span></td>
                    </tr>
                    <tr>
                      <td><strong>Terceros</strong></td>
                      <td>Instaladas por servicios integrados (mapas, vídeo, soporte). Sujetas a sus propias políticas.</td>
                      <td><span className="legal-tag legal-tag--optional">Opcional</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="legal-block">
              <h3>Cómo gestionar las cookies desde tu navegador</h3>
              <div className="legal-browser-grid">
                {[
                  { name: "Chrome", url: "https://support.google.com/chrome/answer/95647" },
                  { name: "Firefox", url: "https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" },
                  { name: "Safari", url: "https://support.apple.com/es-es/guide/safari/sfri11471/mac" },
                  { name: "Edge", url: "https://support.microsoft.com/es-es/windows/eliminar-y-administrar-cookies-168dab11-0753-043d-7c16-ede5947fc64d" },
                ].map(({ name, url }) => (
                  <a key={name} href={url} target="_blank" rel="noreferrer" className="legal-browser-card">
                    <span className="legal-browser-card__name">{name}</span>
                    <span className="legal-browser-card__arrow">→</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="legal-block">
              <h3>Base legal</h3>
              <p>Las cookies técnicas se basan en el interés legítimo del responsable para garantizar el correcto funcionamiento del sitio. El resto requieren tu consentimiento expreso, conforme a la LSSI-CE y el RGPD.</p>
            </div>
          </section>

          {/* ── GESTIÓN DE COOKIES ────────────────────────── */}
          <section id="gestion-cookies" className="legal-section">
            <div className="legal-section__header">
              <h2 className="legal-section__title">Gestión del Consentimiento</h2>
              <span className="legal-section__ref">First-party · localStorage</span>
            </div>

            <div className="legal-callout legal-callout--ok">
              <span className="legal-callout__label">Transparencia</span>
              Al entrar por primera vez en esta web, aparece un aviso de cookies que te permite decidir qué categorías aceptas. Tu decisión se guarda de forma segura y no volvemos a preguntarte hasta que la elimines.
            </div>

            <div className="legal-block">
              <h3>¿Cómo guardamos tu consentimiento?</h3>
              <div className="legal-table-wrap">
                <table className="legal-table">
                  <thead>
                    <tr><th>Mecanismo</th><th>Clave</th><th>Duración</th><th>Contenido</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>localStorage</td>
                      <td><code>daroma_cookie_consent</code></td>
                      <td>Indefinido</td>
                      <td>JSON con preferencias por categoría + fecha</td>
                    </tr>
                    <tr>
                      <td>Cookie first-party</td>
                      <td><code>daroma_cookie_consent</code></td>
                      <td>1 año</td>
                      <td>Mismo JSON · SameSite=Lax</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="legal-block">
              <h3>Opciones del panel de cookies</h3>
              <div className="legal-consent-options">
                <div className="legal-consent-option">
                  <div className="legal-consent-option__icon legal-consent-option__icon--accept">✓</div>
                  <div>
                    <div className="legal-consent-option__title">Aceptar todas</div>
                    <div className="legal-consent-option__desc">Activa todas las categorías: técnicas, analíticas y personalización.</div>
                  </div>
                </div>
                <div className="legal-consent-option">
                  <div className="legal-consent-option__icon legal-consent-option__icon--config">⚙</div>
                  <div>
                    <div className="legal-consent-option__title">Configurar</div>
                    <div className="legal-consent-option__desc">Abre un panel con toggles individuales por categoría para una elección precisa.</div>
                  </div>
                </div>
                <div className="legal-consent-option">
                  <div className="legal-consent-option__icon legal-consent-option__icon--reject">✕</div>
                  <div>
                    <div className="legal-consent-option__title">Solo esenciales</div>
                    <div className="legal-consent-option__desc">Activa únicamente las cookies técnicas imprescindibles. Las analíticas y de personalización quedan desactivadas.</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="legal-block">
              <h3>¿Cómo cambiar tu decisión?</h3>
              <p>Puedes modificar tus preferencias en cualquier momento eliminando la clave <code>daroma_cookie_consent</code> del almacenamiento local de tu navegador. Al recargar la página, el aviso de cookies aparecerá de nuevo.</p>
            </div>

            <div className="legal-contact-box" style={{ marginTop: "24px" }}>
              <div className="legal-contact-box__icon">✉</div>
              <div>
                <div className="legal-contact-box__label">¿Preguntas sobre privacidad o cookies?</div>
                <a href="mailto:info@daromainnovations.com" className="legal-contact-box__value">
                  info@daromainnovations.com
                </a>
              </div>
            </div>
          </section>

        </main>
      </div>

      <GradientLine />
      <Footer />
    </div>
  );
};

export default LegalPage;
